import { Card, Col, Empty, Spin } from "antd";
import CardTitle from "components/CardTitle";
import { routes } from "config/routes";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FaFileAlt } from "react-icons/fa";
import { MdOutlineKeyboardArrowRight } from "react-icons/md";
import { useParams } from "react-router-dom";
import { formatNumberWithSpaces } from "utils/formatNumberWithSpaces";
import { checkStatusTypeColor } from "utils/statusTypes";
import useDashboardApi from "../hooks/useDashboardApi";

export default function LatestModifiedDocs() {
  const { t } = useTranslation();
  const urlParams: RouteParams = useParams();
  const {
    listSaleDocumentsLatestModified,
    lastDocsModified: data,
    listSaleDocumentsLatestModifiedloading: loading,
  } = useDashboardApi();

  useEffect(() => {
    if (urlParams?.companyId) {
      listSaleDocumentsLatestModified({
        variables: {
          filter: { group_CompanyId: urlParams.companyId },
          orderBy: "-updated_at",
          limit: 5,
        },
      });
    }
  }, [urlParams?.companyId]);

  const handleOnClickExtra = () => {
    routes.navigate(`/company/${urlParams?.companyId}/documents/all`);
  };

  const handleOnClick = (id: string) => {
    routes.navigate(`/company/${urlParams?.companyId}/documents/invoices/edit/${id}`);
  };

  const renderDocumentItem = (item: any) => {
    const color = checkStatusTypeColor(item?.status?.value);
    const decimalPlaces = item?.group?.currency?.decimalPlaces;
    const currencyCode = item?.group?.currency?.code;
    const formattedTotalValue = formatNumberWithSpaces(
      Number(item?.netPrice ?? 0).toFixed(decimalPlaces),
    );

    const isNegative = Number(item?.netPrice) < 0;

    return (
      <div key={item?.id} className="document-item" onClick={() => handleOnClick(item?.id)}>
        <div className="document-icon">
          <FaFileAlt />
        </div>

        <div className="document-content">
          <div className="document-date">{item?.date}</div>
          <div className="document-title">
            {item?.type?.label} {t("for")} <strong>{item?.client?.name}</strong>
          </div>
        </div>

        <div className="document-status">
          <div className="status-badge" style={{ backgroundColor: color }}>
            {item?.status?.label}
          </div>
        </div>

        <div className="document-amount">
          <span className={isNegative ? "amount-negative" : ""}>
            {formattedTotalValue} {currencyCode}
            <span className="tax-label">HT</span>
          </span>
        </div>

        <div className="document-arrow">
          <MdOutlineKeyboardArrowRight />
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="documents-loading">
          <Spin size="large" />
        </div>
      );
    }

    if (!data || data.length === 0) {
      return (
        <div className="documents-empty">
          <Empty description={t("No documents found")} />
        </div>
      );
    }

    return <div className="documents-list">{data.map(renderDocumentItem)}</div>;
  };

  return (
    <Col md={12} sm={24} xs={24}>
      <Card
        className="antd-newrai-card documents-card dashboard-custom-card"
        title={<CardTitle icon={<FaFileAlt />} title={t("Latest modified documents")} />}
        extra={
          <span
            onClick={handleOnClickExtra}
            className="see-all-link"
            role="button"
            aria-label={t("See all documents")}
          >
            {t("See all")}
            <MdOutlineKeyboardArrowRight className="see-all-icon" />
          </span>
        }
      >
        {renderContent()}
      </Card>
    </Col>
  );
}
